<template>
  <!--
    IosInstallCoach — one-time Add-to-Home-Screen guide for iOS users (gap GR-3).

    Why this exists:
      On iOS, web push is categorically unavailable until the PWA is installed
      to the Home Screen (Safari 16.4+ only inside an installed/standalone PWA —
      the canRequestPushPermission() gate at usePushNotifications.js:89-95 returns
      false for un-installed iOS Safari). iOS has NO `beforeinstallprompt` API, so
      nothing prompts the user to install. Without this coach the entire push
      install beat is a silent dead-end for every iPhone/iPad viewer: they never
      learn they must tap Share → "Add to Home Screen" first. This sheet teaches
      that gesture visually so the push opt-in moment can actually be reached.

    Two modes (resolved on mount from the UA):
      - 'safari'  → real iOS Safari, not yet installed. Show the Share → A2HS
                    steps (the only browser on iOS that can install a PWA).
      - 'webview' → an in-app webview (Instagram/Facebook/etc.) or a non-Safari
                    iOS browser (Chrome/Firefox iOS) where Share → A2HS is NOT
                    available. Show "open this page in Safari" guidance instead of
                    failing silently (task focus area: in-app webview detection).

    Gating (one-time, snoozed forever after dismissal):
      1. Not TMA (Telegram delivers notifications natively).
      2. Apple device (iPhone / iPad / iPod) — A2HS-for-push is iOS-only.
      3. NOT already standalone (installed) — no point coaching an installed PWA.
      4. localStorage `poolit:ios-install-coach-dismissed` is unset.

    UX mirrors HuaweiBatteryCoach: F7 modal sheet, AR-first copy
    (notification.iosInstallCoach.*), RTL automatic via document dir, two CTAs
    that both flip the dismissal snooze. Mounted at App.vue root so it can appear
    over any route once the gates pass.
  -->
  <f7-sheet
    v-if="isVisible"
    backdrop
    :opened="open"
    :close-by-backdrop-click="false"
    :close-by-outside-click="false"
    :swipe-to-close="false"
    class="ios-install-coach-sheet sheet-scroll sheet-scroll-inner"
    @sheet:closed="onClosed"
  >
    <div class="ios-install-coach-content">
      <!-- Visual cue: Share → Add → Home Screen, the literal A2HS sequence. -->
      <div class="ios-install-coach-visual">
        <f7-icon
          f7="square_arrow_up"
          size="34px"
          color="purple"
        ></f7-icon>
        <f7-icon
          f7="arrow_right"
          size="18px"
          color="gray"
          class="ios-install-coach-visual-arrow"
        ></f7-icon>
        <f7-icon
          f7="plus_app"
          size="34px"
          color="purple"
        ></f7-icon>
        <f7-icon
          f7="arrow_right"
          size="18px"
          color="gray"
          class="ios-install-coach-visual-arrow"
        ></f7-icon>
        <f7-icon
          f7="house_fill"
          size="34px"
          color="purple"
        ></f7-icon>
      </div>

      <template v-if="mode === 'safari'">
        <h2 class="ios-install-coach-title">
          {{ $t('notification.iosInstallCoach.title') }}
        </h2>

        <p class="ios-install-coach-intro">
          {{ $t('notification.iosInstallCoach.intro') }}
        </p>

        <ol class="ios-install-coach-steps">
          <li class="ios-install-coach-step">
            {{ $t('notification.iosInstallCoach.step1') }}
          </li>
          <li class="ios-install-coach-step">
            {{ $t('notification.iosInstallCoach.step2') }}
          </li>
          <li class="ios-install-coach-step">
            {{ $t('notification.iosInstallCoach.step3') }}
          </li>
        </ol>

        <p class="ios-install-coach-tip">
          {{ $t('notification.iosInstallCoach.tip') }}
        </p>
      </template>

      <template v-else>
        <h2 class="ios-install-coach-title">
          {{ $t('notification.iosInstallCoach.webviewTitle') }}
        </h2>

        <p class="ios-install-coach-intro">
          {{ $t('notification.iosInstallCoach.webviewBody') }}
        </p>
      </template>

      <div class="ios-install-coach-actions">
        <f7-button
          large
          fill
          color="purple"
          class="ios-install-coach-action"
          @click="dismiss"
        >
          {{ $t('notification.iosInstallCoach.action') }}
        </f7-button>
        <f7-button
          class="ios-install-coach-link"
          @click="dismiss"
        >
          {{ $t('notification.iosInstallCoach.dismiss') }}
        </f7-button>
      </div>
    </div>
  </f7-sheet>
</template>

<script setup>
import { onMounted, ref } from 'vue';
import { isTma } from '@/platform/index.js';

// localStorage key — same lifecycle rationale as HuaweiBatteryCoach: survives
// only as long as the OS preserves localStorage; a reinstalled browser re-shows
// the coach once. Namespaced under `poolit:`.
const DISMISSED_KEY = 'poolit:ios-install-coach-dismissed';

const isVisible = ref(false);
const open = ref(false);
// 'safari' (show A2HS steps) | 'webview' (show open-in-Safari guidance).
const mode = ref('safari');

/**
 * Is this an Apple touch device (iPhone / iPad / iPod) where A2HS-for-push
 * applies? iPadOS 13+ masquerades as desktop Safari (platform 'MacIntel') so we
 * also accept MacIntel + a touch screen.
 */
function isAppleDevice() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
}

/**
 * Already installed to the Home Screen? Installed PWAs run standalone and push
 * is already available — no install coaching needed.
 */
function isStandalone() {
  if (typeof window === 'undefined') return false;
  const mql = window.matchMedia?.('(display-mode: standalone)');
  return (mql && mql.matches === true) || window.navigator.standalone === true;
}

/**
 * Real iOS Safari (the ONLY iOS browser that can install a PWA + grant push).
 * Returns false for Chrome/Firefox/Edge-iOS and for in-app WKWebviews (which
 * omit the "Version/" + "Safari" UA tokens), routing those to the 'webview'
 * open-in-Safari guidance.
 */
function isIosSafari() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  // Non-Safari iOS browsers carry their own engine token.
  if (/CriOS|FxiOS|EdgiOS|OPiOS|YaBrowser|DuckDuckGo/i.test(ua)) return false;
  // Genuine mobile Safari exposes both "Version/x" and the "Safari" token;
  // in-app webviews (FBAN/Instagram/Line/WKWebView) drop one or both.
  return /Safari/.test(ua) && /Version\//.test(ua);
}

function isDismissed() {
  try {
    return localStorage.getItem(DISMISSED_KEY) === '1';
  } catch {
    // Storage blocked (private mode / quota) — treat as dismissed so we don't
    // pester the user every session. Mirrors HuaweiBatteryCoach.
    return true;
  }
}

function markDismissed() {
  try {
    localStorage.setItem(DISMISSED_KEY, '1');
  } catch {
    // Storage blocked — accept the loss; the v-if gate keeps it single-show
    // in-memory for this session anyway.
  }
}

function dismiss() {
  markDismissed();
  open.value = false;
}

function onClosed() {
  isVisible.value = false;
}

onMounted(() => {
  // Gate stack (cheapest first): TMA → dismissed → Apple device → not installed.
  if (isTma()) return;
  if (isDismissed()) return;
  if (!isAppleDevice()) return;
  if (isStandalone()) return;

  // Resolve the copy variant: real Safari gets the A2HS steps; everything else
  // on iOS (in-app webview, Chrome/Firefox iOS) gets the open-in-Safari nudge
  // so the install beat never fails silently.
  mode.value = isIosSafari() ? 'safari' : 'webview';

  isVisible.value = true;
  // Yield one frame for the v-if mount before the :opened watch fires the open
  // animation (otherwise the backdrop briefly stacks over the loading layout).
  if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
    window.requestAnimationFrame(() => {
      open.value = true;
    });
  } else {
    open.value = true;
  }
});
</script>

<style scoped>
/* Sheet content — vertical stack with comfortable touch targets. Mirrors the
   HuaweiBatteryCoach sheet so the two coaches read as one design family. */
.ios-install-coach-sheet {
  --f7-sheet-bg-color: #ffffff;
  --f7-sheet-border-color: transparent;
  height: auto;
  max-height: 90%;
}

.ios-install-coach-content {
  padding:
    24px
    max(20px, env(safe-area-inset-right))
    max(20px, env(safe-area-inset-bottom))
    max(20px, env(safe-area-inset-left));
  display: flex;
  flex-direction: column;
  gap: 14px;
  text-align: start;
}

.ios-install-coach-visual {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 4px;
}

.ios-install-coach-visual-arrow {
  /* Mirror the directional arrow in RTL so the Share → Add → Home sequence
     reads correctly in Arabic. The RTL rule lives in a separate non-scoped
     <style> block below — Vue's :global() pseudo-class does NOT support a
     compound selector like :global(parent) .child inside <style scoped>; the
     compiler silently drops the child part and emits bare `html[dir='rtl'] {}`
     which transforms the entire page. */
  transform: scaleX(1);
}

.ios-install-coach-title {
  margin: 0;
  font-size: 19px;
  font-weight: 700;
  color: #1a1a1a;
  text-align: center;
}

.ios-install-coach-intro {
  margin: 0;
  font-size: 15px;
  line-height: 1.55;
  color: #444444;
}

.ios-install-coach-steps {
  margin: 0;
  padding-inline-start: 24px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.ios-install-coach-step {
  font-size: 14.5px;
  line-height: 1.55;
  color: #1a1a1a;
}

.ios-install-coach-tip {
  margin: 4px 0 0 0;
  font-size: 13px;
  line-height: 1.45;
  color: #777777;
  text-align: center;
}

.ios-install-coach-actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
}

.ios-install-coach-action {
  width: 100%;
  height: 48px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
}

.ios-install-coach-link {
  color: #777777;
  font-size: 14px;
}
</style>

<!-- Non-scoped: rules that must target html[dir='rtl'] on <html> cannot use
     Vue's :global() inside <style scoped> — the compiler drops the child
     selector and emits a bare html[dir='rtl'] {} that mirrors the whole page.
     Use a separate unscoped <style> block instead (same pattern as PoolShareHero.vue). -->
<style>
[dir='rtl'] .ios-install-coach-visual-arrow {
  transform: scaleX(-1);
}
</style>
